import React, { useMemo, useRef, useState, useEffect } from "react";
import { useDimensions } from "common/hooks";
import styled from "styled-components";
import * as d3 from "d3";
import Legend from "../Legend";
import Tooltip from "./Tooltip";

const StyledLegendContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: flex-end !important;
  padding-right: 25px;
  z-index: 10;
  }
`;

const EmptyContainer = styled.div`
   width:100%;
  height: ${props => props.height}px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Wrapper = styled.div``;

const WrapperChart = styled.div`
  width: 100%;
  height: 100%;
  position: relative;

  svg {
    width: 100%;
    height: 100%;

    .intervalChartAxis {
      fill: var(--chart-axis-text-color);
      color: #929295;
      font-family: var(--chart-font-family, Arial, sans-serif);
      font-size: 9px !important;

      text {
      font-size: 9px !important;
      }
    }
  }
`;

const formatDate = d3.timeFormat("%m/%d %H:%M");
const formatDateTooltip = d3.timeFormat("%Y-%m-%d  %H:%M");
const LINE_GAP = 5;
const LINE_WIDTH_MIN = 3;
const LINE_WIDTH_INI = 24;
const LEGEND_FIELDS = [
  {
    name: "volume",
    label: "Volume",
    color: "#d62728",
  },
  {
    name: "syn-rcv",
    label: "Syn-Rcv",
    color: "#1f78b4",
  },
  {
    name: "syn-snt",
    label: "Syn-Snt",
    color: "#a6cee3",
  },
];

const KEY_TYPE_MAP = {
  volume: 0,
  "syn-rcv": 1,
  "syn-snt": 2, 
}


const XAxisLine = ({ width, height }) => {
  return (
    <line
      x1={0}
      x2={width}
      y1={height}
      y2={height}
      stroke="var(--chart-axis-text-color)"
    />
  );
};

const XAxisEntry = ({ x, heightChart, label, showTick }) => {
  return (
    <>
      {showTick ? (
        <line
          x1={x}
          x2={x}
          y1={0}
          y2={heightChart}
          stroke="var(--chart-grid-color)"
        />
      ) : null}
      <text
        x={x}
        y={heightChart + 20}
        fontSize={9}
        textAnchor="middle"
        className="heatMapAxis"
      >
        {label}
      </text>
    </>
  );
};

const getLineHeight = (height, levelCount) => {
  let lineHeight = LINE_WIDTH_INI;
  while (
    levelCount * (LINE_GAP + lineHeight) > height &&
    lineHeight > LINE_WIDTH_MIN
  ) {
    lineHeight = Math.floor((lineHeight * 2) / 3);
  }
  return lineHeight;
};

function calculateIntervalLevels(intervals) {
  const levels = [];
  const computeLevel = (iniDate) => {
    const iniMoment = moment(iniDate);

    for (let i = 0; i < levels.length; i++) {
      const level = levels[i];
      if (level.length === 0 || iniMoment.isAfter(level[level.length - 1].finDate)) {
        return i;
      }
    }

    levels.push([]);
    return levels.length - 1;
  };

  const intervalsWithLevels = [];
  for (const interval of intervals) {
    const levelIndex = computeLevel(interval.iniDate);
    const intervalWithLevel = { ...interval, level: levelIndex };

    levels[levelIndex].push(intervalWithLevel);
    intervalsWithLevels.push(intervalWithLevel);
  }

  return intervalsWithLevels;
}

function IntervalChart({ data, range }) {
  const chartRef = useRef(null);
  const dimensions = useDimensions(chartRef);
  const { width } = dimensions;
  const height = 500;
  const margin =
    width > 700
      ? { top: 40, right: 50, bottom: 70, left: 50 }
      : { top: 40, right: 10, bottom: 70, left: 10 };
  const widthChart = width - margin.left - margin.right;
  const heightChart = height - margin.top - margin.bottom;

  const [filterFields, setFilterFields] = useState(
    new Set(LEGEND_FIELDS.map((field) => field.name))
  );
  const [hoveredCell, setHoveredCell] = useState(null);

  // The level calculation should only run when the core data changes.
  // The `useDosOverTimeData` hook in Report.jsx already sorts the data by `iniDate`.
  const dataWithLevels = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }
    return calculateIntervalLevels(data);
  }, [data]);

  // Filter data to only include intervals visible in the current time range.
  const visibleData = useMemo(() => {
    if (!range || !dataWithLevels || dataWithLevels.length === 0) {
      return [];
    }
    return dataWithLevels.filter(
      (item) => item.iniDate < range.to && item.finDate > range.from
    );
  }, [dataWithLevels, range]);

  // // Group the visible data by level for rendering.
  const levels = useMemo(() => {
    const grouped = [];
    visibleData.forEach((item) => {
      if (!grouped[item.level]) {
        grouped[item.level] = [];
      }
      grouped[item.level].push(item);
    });
    return grouped;
  }, [visibleData]);

  if (!levels || levels.length === 0) {
    return <EmptyContainer width={500} height={height}>No Attacks Found</EmptyContainer>;
  }

  const xScale = useMemo(() => {
    return d3.scaleTime().range([0, widthChart]).domain([range.from, range.to]);
  }, [range, widthChart]);

  const allTicks = xScale.ticks(Math.floor(widthChart / 110)).map((tick, i) => {
    if (xScale(tick) > 5 && xScale(tick) < widthChart - 5) {
      return (
        <XAxisEntry
          x={xScale(tick)}
          heightChart={heightChart}
          label={formatDate(tick)}
          showTick={true}
          key={formatDate(tick)}
        />
      );
    }

    return null;
  });

  const lineHeight = getLineHeight(heightChart, levels.length);
  const selectedTypes = [...filterFields].map((field) => KEY_TYPE_MAP[field]);

  const allShapes = levels.flatMap((items) => {
    return items.map((item) => {
      const { iniDate, finDate, type, level, details, duration } = item;
      const x = xScale(Math.max(item.iniDate, range.from));
      const xEnd = xScale(Math.min(item.finDate, range.to));
      const y = heightChart - (level + 1) * (lineHeight + LINE_GAP);
      const color = LEGEND_FIELDS[type].color;
      const rectWidth = xEnd - x;
      if (rectWidth <= 0 || !selectedTypes.includes(type)) return null;
      return (
        <g key={`${iniDate.getTime()}-${level}-${type}`}>
          <rect
            x={x}
            y={y}
            width={rectWidth + 2}
            height={lineHeight}
            fill={color}
            onMouseEnter={() => {
              setHoveredCell({
                duration,
                details,
                xPos: x,
                yPos: y,
                date: formatDateTooltip(iniDate)
              })
            }}
            onMouseLeave={() => setHoveredCell(null)}
          />
        </g>
      );
      return item;
    });
  });

  return (
    <Wrapper>
      <StyledLegendContainer className="legend-graph-live">
        <Legend
          fields={LEGEND_FIELDS}
          onChange={(filter) => {
            setFilterFields(filter)
          }}
          value={filterFields}
        />
      </StyledLegendContainer>
      <WrapperChart ref={chartRef} style={{ height }}>
        <svg>
          <g
            width={width}
            height={height}
            transform={`translate(${[margin.left, margin.top].join(",")})`}
          >
            <g className="intervalChartAxis">
              {allTicks}
              <XAxisLine width={widthChart} height={heightChart} />
            </g>
            <g className="intervalls">{allShapes}</g>
          </g>
        </svg>
      </WrapperChart>
      <Tooltip interactionData={hoveredCell} width={width} height={height} />
    </Wrapper>
  );
}

export default IntervalChart;
